import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { AlertTriangle, Loader2, Play, ShieldCheck, Database, RefreshCcw, Search, ChevronDown, ChevronRight, ArrowLeft, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Historical one-shot ops migrations (merged from the old /admin/migration-tools page).
// Each is idempotent; dry_run=true query param previews without committing.
const ONESHOT_MIGRATIONS = [
  {
    id: "invoice-qr-backfill",
    name: "Backfill invoice PayNow QR codes",
    description: "Rewrites old invoices that had 'Pay via PayNow to UEN' with the new scannable EMVCo SGQR QR code block. Surgical — doesn't touch fee math or totals.",
    endpoint: "/payments/admin/backfill-invoice-qr",
  },
  {
    id: "rejected-to-unpaid",
    name: "Migrate rejected → unpaid status",
    description: "Changes any payment rows still labelled 'rejected' to 'unpaid' so they stay visible for follow-up.",
    endpoint: "/payments/admin/bulk-update-rejected-to-unpaid",
  },
  {
    id: "dedupe-duplicate-players",
    name: "Dedupe duplicate academy players",
    description: "Merges duplicate academy_players records into the earliest record per (name + birthdate) cluster. Burns the duplicate membership numbers permanently so chronological order is never corrupted.",
    endpoint: "/academy/admin/dedupe-duplicate-players",
  },
  {
    id: "backfill-team-and-age-group",
    name: "Backfill team + age_group on academy players and sessions",
    description: "Restores broken linkage: tags Term 2 session templates and weekly instances with team_id, computes age_group from birthdate, auto-assigns currently-unassigned players to the lowest matching team. Idempotent.",
    endpoint: "/academy/admin/backfill-team-and-age-group",
  },
  {
    id: "backfill-orphan-fee-breakdown",
    name: "Backfill missing fee_breakdown on registrations + transactions",
    description: "Computes a per-child line-item fee_breakdown from registration data and writes it to academy_parent_registrations + their linked payment_transactions where it's currently null. Idempotent and surgical.",
    endpoint: "/academy/admin/backfill-orphan-fee-breakdown",
  },
];

export default function AdminMigrations() {
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  // Fee-Breakdown Shape Audit state
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [openShapeRank, setOpenShapeRank] = useState(null);

  // Fee-Breakdown v1 backfill state
  const [bf, setBf] = useState(null);
  const [bfLoading, setBfLoading] = useState(false);
  const [bfCommitting, setBfCommitting] = useState(false);
  const [bfResult, setBfResult] = useState(null);
  const [bfOpenShape, setBfOpenShape] = useState(null);

  // Status field audit state
  const [sfa, setSfa] = useState(null);
  const [sfaLoading, setSfaLoading] = useState(false);

  // Status collapse migration state
  const [sc, setSc] = useState(null);
  const [scLoading, setScLoading] = useState(false);
  const [scCommitting, setScCommitting] = useState(false);
  const [scResult, setScResult] = useState(null);

  // Registration link audit state
  const [rla, setRla] = useState(null);
  const [rlaLoading, setRlaLoading] = useState(false);
  const [rlaTab, setRlaTab] = useState("orphan"); // 'orphan' | 'stale'

  // Registration link backfill (auto-link) state
  const [rlb, setRlb] = useState(null);
  const [rlbLoading, setRlbLoading] = useState(false);
  const [rlbCommitting, setRlbCommitting] = useState(false);
  const [rlbResult, setRlbResult] = useState(null);
  const [rlbTab, setRlbTab] = useState("auto_link"); // 'auto_link' | 'ambiguous' | 'legacy' | 'stale'

  // Canonical amount audit state
  const [caa, setCaa] = useState(null);
  const [caaLoading, setCaaLoading] = useState(false);
  const [caaOpenField, setCaaOpenField] = useState(null);

  // Retire amount duplicates state (Step 5)
  const [rad, setRad] = useState(null);
  const [radLoading, setRadLoading] = useState(false);
  const [radCommitting, setRadCommitting] = useState(false);
  const [radResult, setRadResult] = useState(null);

  // Scheduled cleanup state (Step 6)
  const [sclRunning, setSclRunning] = useState(false);
  const [sclResult, setSclResult] = useState(null);
  const [sclLastRuns, setSclLastRuns] = useState(null);
  const [sclLastLoading, setSclLastLoading] = useState(false);

  // Historical one-shot migrations state (merged from old /admin/migration-tools)
  const [osmRunning, setOsmRunning] = useState({}); // id -> "dry"|"run"|false
  const [osmResults, setOsmResults] = useState({}); // id -> last result payload
  const [osmOpen, setOsmOpen] = useState(false);

  const callOneshotMigration = async (mig, dryRun) => {
    setOsmRunning(prev => ({ ...prev, [mig.id]: dryRun ? "dry" : "run" }));
    try {
      const url = `${API}${mig.endpoint}${dryRun ? "?dry_run=true" : ""}`;
      const res = await axios.post(url, {}, { withCredentials: true });
      setOsmResults(prev => ({ ...prev, [mig.id]: { ...res.data, at: new Date().toISOString(), dry: dryRun } }));
      if (dryRun) {
        toast.info(`Dry-run: ${res.data.total_candidates ?? res.data.updated ?? 0} candidate(s)`);
      } else {
        toast.success(`Migration complete — ${res.data.updated ?? 0} updated, ${res.data.errored ?? 0} error(s)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || `Migration failed: ${err.message}`);
      setOsmResults(prev => ({ ...prev, [mig.id]: { error: err.response?.data?.detail || err.message, at: new Date().toISOString(), dry: dryRun } }));
    } finally {
      setOsmRunning(prev => ({ ...prev, [mig.id]: false }));
    }
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    setResult(null);
    try {
      const res = await axios.get(`${API}/academy/admin/migrations/consolidate-registrations`, { withCredentials: true });
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load preview");
      setPreview({ error: err.response?.data?.detail || String(err) });
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => { loadPreview(); }, []);



  const runShapeAudit = async () => {
    await Promise.resolve();
    setAuditLoading(true);
    setOpenShapeRank(null);
    try {
      const res = await axios.get(`${API}/academy/admin/migrations/fee-breakdown-shape-audit`, { withCredentials: true });
      setAudit(res.data);
      toast.success(`Audit complete — ${res.data.distinct_shapes_count} distinct shape(s) across ${res.data.totals?.total_payment_transactions} txns`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Shape audit failed");
      setAudit({ error: err.response?.data?.detail || String(err) });
    } finally {
      setAuditLoading(false);
    }
  };

  const copyAuditJson = () => {
    if (!audit) return;
    try {
      navigator.clipboard.writeText(JSON.stringify(audit, null, 2));
      toast.success("Audit JSON copied to clipboard");
    } catch {
      toast.error("Copy failed — select the JSON manually");
    }
  };

  const loadBfPreview = async () => {
    setBfLoading(true);
    try {
      const res = await axios.get(`${API}/academy/admin/migrations/fee-breakdown-v1-backfill`, { withCredentials: true });
      setBf(res.data);
      setBfResult(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill preview failed");
    } finally {
      setBfLoading(false);
    }
  };

  const runBfCommit = async () => {
    if (!bf?.confirm_token) { toast.error("No confirm token — re-run preview"); return; }
    if (!window.confirm(`Backfill ${bf.legacy_to_convert_count} legacy + ${bf.null_to_init_count} null fee_breakdown rows to v1. Continue?`)) return;
    setBfCommitting(true);
    try {
      const res = await axios.post(
        `${API}/academy/admin/migrations/fee-breakdown-v1-backfill/commit`,
        { confirm_token: bf.confirm_token },
        { withCredentials: true },
      );
      setBfResult(res.data);
      setBf(null);
      toast.success(`Backfill complete — ${res.data.converted_legacy} legacy + ${res.data.initialised_null} null rows migrated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill commit failed");
    } finally {
      setBfCommitting(false);
    }
  };

  const runStatusFieldAudit = async () => {
    setSfaLoading(true);
    setSfa(null);
    try {
      const res = await axios.get(`${API}/academy/admin/migrations/status-field-audit`, { withCredentials: true });
      setSfa(res.data);
      toast.success(`Status audit complete — ${res.data.totals?.agree_count}/${res.data.totals?.total_payment_transactions} rows agree`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Status audit failed");
      setSfa({ error: err.response?.data?.detail || String(err) });
    } finally {
      setSfaLoading(false);
    }
  };

  const copyStatusAuditJson = () => {
    if (!sfa) return;
    try {
      navigator.clipboard.writeText(JSON.stringify(sfa, null, 2));
      toast.success("Status audit JSON copied to clipboard");
    } catch {
      toast.error("Copy failed — select the JSON manually");
    }
  };

  const loadScPreview = async () => {
    setScLoading(true);
    try {
      const res = await axios.get(`${API}/academy/admin/migrations/status-collapse`, { withCredentials: true });
      setSc(res.data);
      setScResult(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Status collapse preview failed");
    } finally {
      setScLoading(false);
    }
  };

  const runScCommit = async () => {
    if (!sc?.confirm_token) { toast.error("No confirm token — re-run preview"); return; }
    if (!window.confirm(`Collapse status into payment_status across ${sc.to_collapse_count} rows. Rename existing status → _legacy_status. Continue?`)) return;
    setScCommitting(true);
    try {
      const res = await axios.post(
        `${API}/academy/admin/migrations/status-collapse/commit`,
        { confirm_token: sc.confirm_token },
        { withCredentials: true },
      );
      setScResult(res.data);
      setSc(null);
      toast.success(`Status collapse complete — ${res.data.collapsed_count} rows migrated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Status collapse commit failed");
    } finally {
      setScCommitting(false);
    }
  };

  const runRegistrationLinkAudit = async () => {
    setRlaLoading(true);
    setRla(null);
    try {
      const res = await axios.get(`${API}/academy/admin/migrations/registration-link-audit`, { withCredentials: true });
      setRla(res.data);
      toast.success(`Audit complete — ${res.data.totals?.registration_id_null} null + ${res.data.totals?.registration_id_stale} stale of ${res.data.totals?.total_payment_transactions} txns`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration link audit failed");
      setRla({ error: err.response?.data?.detail || String(err) });
    } finally {
      setRlaLoading(false);
    }
  };

  const copyRlaJson = () => {
    if (!rla) return;
    try {
      navigator.clipboard.writeText(JSON.stringify(rla, null, 2));
      toast.success("Audit JSON copied to clipboard");
    } catch {
      toast.error("Copy failed — select the JSON manually");
    }
  };

  const loadRlbPreview = async () => {
    setRlbLoading(true);
    setRlbResult(null);
    try {
      const res = await axios.get(`${API}/academy/admin/migrations/registration-link-backfill`, { withCredentials: true });
      setRlb(res.data);
      toast.success(`Preview ready — ${res.data.auto_link_count} auto · ${res.data.ambiguous_count} ambiguous · ${res.data.legacy_count} legacy · ${res.data.stale_count} stale`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill preview failed");
    } finally {
      setRlbLoading(false);
    }
  };

  const runRlbCommit = async () => {
    if (!rlb?.confirm_token) { toast.error("No confirm token — re-run preview"); return; }
    const summary =
      `Auto-link ${rlb.auto_link_count} row(s) by writing registration_id.\n` +
      `Mark ${rlb.legacy_count} unlinkable row(s) as _registration_link_legacy.\n` +
      `Mark ${rlb.stale_count} stale row(s) as _registration_link_stale.\n` +
      `Skip ${rlb.ambiguous_count} ambiguous row(s) (manual review).\n\nProceed?`;
    if (!window.confirm(summary)) return;
    setRlbCommitting(true);
    try {
      const res = await axios.post(
        `${API}/academy/admin/migrations/registration-link-backfill/commit`,
        { confirm_token: rlb.confirm_token },
        { withCredentials: true },
      );
      setRlbResult(res.data);
      setRlb(null);
      toast.success(`Backfill complete — ${res.data.auto_linked_count} linked · ${res.data.legacy_marked_count} legacy · ${res.data.stale_marked_count} stale`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill commit failed");
    } finally {
      setRlbCommitting(false);
    }
  };

  const runCaaAudit = async () => {
    setCaaLoading(true);
    setCaa(null);
    try {
      const res = await axios.get(`${API}/academy/admin/migrations/canonical-amount-audit`, { withCredentials: true });
      setCaa(res.data);
      toast.success(`Audit complete — canonical recommendation: ${res.data.recommendation?.canonical_field}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Canonical amount audit failed");
      setCaa({ error: err.response?.data?.detail || String(err) });
    } finally {
      setCaaLoading(false);
    }
  };

  const copyCaaJson = () => {
    if (!caa) return;
    try {
      navigator.clipboard.writeText(JSON.stringify(caa, null, 2));
      toast.success("Audit JSON copied to clipboard");
    } catch {
      toast.error("Copy failed — select the JSON manually");
    }
  };

  const loadRadPreview = async () => {
    setRadLoading(true);
    setRadResult(null);
    try {
      const res = await axios.get(`${API}/academy/admin/migrations/retire-amount-duplicates`, { withCredentials: true });
      setRad(res.data);
      toast.success(`Preview ready — ${res.data.to_mutate_count} row(s) to retire`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Retire-amount-duplicates preview failed");
    } finally {
      setRadLoading(false);
    }
  };

  const runRadCommit = async () => {
    if (!rad?.confirm_token) { toast.error("No confirm token — re-run preview"); return; }
    if (!window.confirm(
      `Retire amount-duplicate fields on ${rad.to_mutate_count} row(s).\n\n` +
      `- received_amount → _legacy_received_amount (${rad.has_received_amount_count} rows)\n` +
      `- expected_amount → _legacy_expected_amount (${rad.has_expected_amount_count} rows)\n` +
      `- amount_received and amount are NEVER touched.\n\nProceed?`
    )) return;
    setRadCommitting(true);
    try {
      const res = await axios.post(
        `${API}/academy/admin/migrations/retire-amount-duplicates/commit`,
        { confirm_token: rad.confirm_token },
        { withCredentials: true },
      );
      setRadResult(res.data);
      setRad(null);
      toast.success(`Retired — ${res.data.rows_mutated} row(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Retire-amount-duplicates commit failed");
    } finally {
      setRadCommitting(false);
    }
  };

  const runScheduledCleanup = async () => {
    if (!window.confirm("Run the scheduled cleanup now? Archives abandoned checkouts (>24h, status=initiated) and stale draft registrations (>14d). Idempotent.")) return;
    setSclRunning(true);
    try {
      const res = await axios.post(`${API}/academy/admin/migrations/scheduled-cleanup/trigger`, {}, { withCredentials: true });
      setSclResult(res.data);
      toast.success(`Cleanup complete — ${res.data.abandoned_checkouts_archived} checkout(s), ${res.data.stale_drafts_archived} draft(s) archived`);
      // refresh last-runs list
      loadSclLastRuns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scheduled cleanup failed");
    } finally {
      setSclRunning(false);
    }
  };

  const loadSclLastRuns = async () => {
    setSclLastLoading(true);
    try {
      const res = await axios.get(`${API}/academy/admin/migrations/scheduled-cleanup/last-runs`, { withCredentials: true });
      setSclLastRuns(res.data.runs || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load last-runs");
    } finally {
      setSclLastLoading(false);
    }
  };

  const runCommit = async () => {
    if (!preview?.confirm_token) {
      toast.error("No confirm token. Re-load preview first.");
      return;
    }
    if (!window.confirm(
      `This will INSERT ${preview.candidate_count} record(s) into academy_parent_registrations.\n\n` +
      `- Only INSERTs (no UPDATEs). Existing rows are never overwritten.\n` +
      `- Source collection (academy_registrations) is NEVER modified.\n` +
      `- Each inserted row stamped with _migrated_from + _migrated_at for audit.\n\n` +
      `Proceed?`
    )) return;

    setCommitting(true);
    try {
      const res = await axios.post(
        `${API}/academy/admin/migrations/consolidate-registrations/commit`,
        { confirm_token: preview.confirm_token },
        { withCredentials: true },
      );
      setResult(res.data);
      toast.success(`Inserted ${res.data.inserted} record(s). Skipped (collisions): ${res.data.skipped_collisions?.length || 0}`);
      // Reload preview so the screen reflects the new state
      await loadPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Migration failed");
      setResult({ error: err.response?.data?.detail || String(err) });
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6" data-testid="admin-migrations-page">
      <div className="max-w-5xl mx-auto">
        <Button
          onClick={() => { if (window.history.length > 1) { window.history.back(); } else { window.location.href = "/"; } }}
          variant="outline"
          size="sm"
          className="border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800 mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex items-center gap-2 mb-2">
          <Database className="w-6 h-6 text-emerald-400" />
          <h1 className="text-2xl font-semibold">Admin · Data migrations</h1>
        </div>
        <p className="text-sm text-slate-400 mb-6">
          One-time data fixes. Each migration is gated by a dry-run preview and a short-lived confirm token. No row is ever overwritten — migrations only add or normalise.
        </p>

        {/* Consolidate registrations card */}
        <Card className="bg-slate-900/40 border-slate-800 p-5 mb-4" data-testid="consolidate-registrations-card">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <h2 className="text-lg font-semibold text-emerald-300">Consolidate registrations</h2>
              <p className="text-xs text-slate-400 mt-1 max-w-2xl">
                Copies rows that exist only in <code className="text-amber-300">academy_registrations</code> into
                {" "}<code className="text-emerald-300">academy_parent_registrations</code>, preserving IDs.
                Does not modify <code>fee_breakdown</code>, <code>status</code>, or any join key.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={loadPreview}
                disabled={previewLoading || committing}
                variant="outline"
                size="sm"
                className="border-slate-700"
                data-testid="reload-preview-btn"
              >
                <RefreshCcw className={`w-4 h-4 mr-1 ${previewLoading ? "animate-spin" : ""}`} /> Reload preview
              </Button>
            </div>
          </div>

          {previewLoading && !preview ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading preview…</div>
          ) : preview?.error ? (
            <div className="text-rose-300 text-sm">Error: {String(preview.error)}</div>
          ) : preview ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Stat label="academy_registrations" value={preview.academy_registrations_count} accent="amber" />
                <Stat label="academy_parent_registrations" value={preview.academy_parent_registrations_count} accent="emerald" />
                <Stat label="In both (untouched)" value={preview.in_both} accent="slate" />
                <Stat label="Candidates to copy" value={preview.candidate_count} accent={preview.candidate_count > 0 ? "blue" : "slate"} />
              </div>

              {/* Rules box */}
              <div className="border border-slate-800 bg-slate-900/40 rounded p-3 mb-4">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-emerald-400" /> Safety rules enforced by the endpoint</div>
                <ul className="text-[11px] text-slate-300 space-y-1 list-disc list-inside">
                  {(preview.rules || []).map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>

              {/* Candidate table */}
              {preview.candidate_count > 0 ? (
                <div className="border border-slate-800 rounded overflow-hidden mb-4 max-h-[420px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-900/70 text-slate-400 sticky top-0">
                      <tr>
                        <th className="px-2 py-2 text-left">#</th>
                        <th className="px-2 py-2 text-left">Registration ID</th>
                        <th className="px-2 py-2 text-left">Parent</th>
                        <th className="px-2 py-2 text-left">Term</th>
                        <th className="px-2 py-2 text-right">Total fee</th>
                        <th className="px-2 py-2 text-left">Status</th>
                        <th className="px-2 py-2 text-left">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.candidates.map((c, i) => (
                        <tr key={c.id} className="border-t border-slate-800 hover:bg-slate-900/30" data-testid={`candidate-row-${i}`}>
                          <td className="px-2 py-2 text-slate-500">{i + 1}</td>
                          <td className="px-2 py-2 font-mono text-[10px] text-emerald-300">{c.id}</td>
                          <td className="px-2 py-2">
                            <div className="text-slate-200">{c.parent_name || "—"}</div>
                            <div className="text-[10px] text-slate-500">{c.parent_email}</div>
                          </td>
                          <td className="px-2 py-2 text-slate-300">{c.term_name || c.term_id?.slice(0, 8) || "—"}</td>
                          <td className="px-2 py-2 text-right text-slate-200 font-mono">{c.total_fee === null || c.total_fee === undefined ? "—" : `$${Number(c.total_fee).toFixed(2)}`}</td>
                          <td className="px-2 py-2"><Badge variant="outline" className="text-[9px] border-slate-700 text-slate-400">{c.status || "—"}</Badge></td>
                          <td className="px-2 py-2 text-[10px] text-slate-500">{(c.created_at || "").slice(0, 10)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center text-slate-500 italic py-4 text-sm" data-testid="nothing-to-migrate">
                  ✓ Nothing to migrate. Every <code>academy_registrations</code> id is already present in <code>academy_parent_registrations</code>.
                </div>
              )}

              {/* Commit panel */}
              {preview.candidate_count > 0 && preview.confirm_token && !result && (
                <div className="border border-blue-500/40 bg-blue-500/5 rounded p-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-xs text-blue-200 flex-1 min-w-[260px]">
                    Confirm token issued (TTL {Math.round(preview.confirm_token_ttl_seconds / 60)} min). Click &quot;Run migration&quot; to insert <strong>{preview.candidate_count}</strong> record(s).
                  </div>
                  <Button
                    onClick={runCommit}
                    disabled={committing}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40"
                    data-testid="run-migration-btn"
                  >
                    {committing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
                    Run migration
                  </Button>
                </div>
              )}

              {result && (
                <div className={`border rounded p-3 text-xs ${result.error ? "border-rose-500/40 bg-rose-500/5" : "border-emerald-500/40 bg-emerald-500/5"}`} data-testid="migration-result">
                  {result.error ? (
                    <div className="text-rose-300">Migration failed: {String(result.error)}</div>
                  ) : (
                    <>
                      <div className="text-emerald-300 font-semibold flex items-center gap-1"><ShieldCheck className="w-4 h-4" /> Migration complete</div>
                      <div className="text-slate-300 mt-1">
                        Inserted: <strong>{result.inserted}</strong>
                        {" "}· Skipped (collisions): <strong>{result.skipped_collisions?.length || 0}</strong>
                        {" "}· Completed at: <span className="font-mono">{result.completed_at}</span>
                      </div>
                      {(result.skipped_collisions?.length || 0) > 0 && (
                        <details className="mt-1">
                          <summary className="text-amber-300 cursor-pointer">Collisions ({result.skipped_collisions.length})</summary>
                          <pre className="text-[10px] text-amber-200 whitespace-pre-wrap mt-1">{result.skipped_collisions.join("\n")}</pre>
                        </details>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          ) : null}
        </Card>

        {/* Fee-Breakdown Shape Audit card */}
        <Card className="bg-slate-900/40 border-slate-800 p-5 mb-4" data-testid="fee-breakdown-shape-audit-card">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <h2 className="text-lg font-semibold text-sky-300">Fee-breakdown shape audit</h2>
              <p className="text-xs text-slate-400 mt-1 max-w-2xl">
                Read-only diagnostic. Walks the entire <code className="text-amber-300">payment_transactions</code> collection in this environment and reports the distinct top-level key sets in <code>fee_breakdown</code>. PII (names, emails, phones, membership numbers) is redacted in samples. No row is modified.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={runShapeAudit}
                disabled={auditLoading}
                className="bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-40"
                size="sm"
                data-testid="run-shape-audit-btn"
              >
                {auditLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
                Run shape audit
              </Button>
              {audit && !audit.error && (
                <Button onClick={copyAuditJson} variant="outline" size="sm" className="border-slate-700" data-testid="copy-audit-json-btn">
                  Copy JSON
                </Button>
              )}
            </div>
          </div>

          {auditLoading && !audit ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Scanning payment_transactions…</div>
          ) : audit?.error ? (
            <div className="text-rose-300 text-sm" data-testid="shape-audit-error">Error: {String(audit.error)}</div>
          ) : audit ? (
            <>
              {/* Totals */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Stat label="Total txns" value={audit.totals?.total_payment_transactions} accent="slate" />
                <Stat label="With fee_breakdown" value={audit.totals?.with_fee_breakdown_dict} accent="emerald" />
                <Stat label="fee_breakdown = null" value={audit.totals?.with_fee_breakdown_null} accent="amber" />
                <Stat label="Distinct shapes" value={audit.distinct_shapes_count} accent="blue" />
              </div>

              {/* By payment_type */}
              <div className="border border-slate-800 rounded overflow-hidden mb-4">
                <table className="w-full text-xs" data-testid="audit-by-payment-type">
                  <thead className="bg-slate-900/70 text-slate-400">
                    <tr>
                      <th className="px-2 py-2 text-left">payment_type</th>
                      <th className="px-2 py-2 text-right">total</th>
                      <th className="px-2 py-2 text-right">with fee_breakdown</th>
                      <th className="px-2 py-2 text-right">without</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(audit.by_payment_type || []).map((row, i) => (
                      <tr key={i} className="border-t border-slate-800">
                        <td className="px-2 py-2 font-mono text-slate-200">{row.payment_type ?? <span className="text-slate-500 italic">(null)</span>}</td>
                        <td className="px-2 py-2 text-right text-slate-300">{row.total}</td>
                        <td className="px-2 py-2 text-right text-emerald-300">{row.with_fee_breakdown}</td>
                        <td className={`px-2 py-2 text-right ${row.without_fee_breakdown > 0 ? "text-amber-300" : "text-slate-500"}`}>{row.without_fee_breakdown}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Distinct shape list */}
              <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Distinct shapes (click to expand)</div>
              <div className="space-y-2 max-h-[480px] overflow-y-auto">
                {(audit.shapes || []).map((s) => {
                  const isOpen = openShapeRank === s.rank;
                  return (
                    <div key={s.rank} className="border border-slate-800 rounded bg-slate-900/30" data-testid={`shape-row-${s.rank}`}>
                      <button
                        type="button"
                        onClick={() => setOpenShapeRank(isOpen ? null : s.rank)}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-slate-900/50"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {isOpen ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}
                          <Badge variant="outline" className="text-[10px] border-sky-500/50 text-sky-300">#{s.rank}</Badge>
                          <span className="text-xs text-slate-300">count: <strong className="text-slate-100">{s.count}</strong></span>
                          <span className="text-xs text-slate-500">·</span>
                          <span className="text-xs text-slate-400">{s.key_count} keys</span>
                          <span className="text-xs text-slate-500">·</span>
                          <span className="text-[10px] text-slate-400 truncate">
                            {Object.entries(s.payment_types || {}).map(([pt, n]) => `${pt}:${n}`).join("  ")}
                          </span>
                        </div>
                      </button>
                      {isOpen && (
                        <div className="px-3 pb-3 pt-1 border-t border-slate-800">
                          <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">keys</div>
                          <div className="flex flex-wrap gap-1 mb-3">
                            {s.keys.map((k) => (
                              <code key={k} className="text-[10px] bg-slate-800/70 border border-slate-700 text-slate-200 px-1.5 py-0.5 rounded">{k}</code>
                            ))}
                          </div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">redacted sample</div>
                          <pre className="text-[10px] bg-slate-950/80 border border-slate-800 rounded p-2 overflow-x-auto text-slate-300" data-testid={`shape-sample-${s.rank}`}>{JSON.stringify(s.sample_redacted, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-3 text-[10px] text-slate-500 italic">
                Generated at: <span className="font-mono">{audit.generated_at}</span> · audit_version: <span className="font-mono">{audit.audit_version}</span>
              </div>
            </>
          ) : (
            <div className="text-slate-500 text-sm italic" data-testid="audit-empty-state">
              Click &quot;Run shape audit&quot; to scan this environment&apos;s payment_transactions collection.
            </div>
          )}
        </Card>

        {/* Fee-Breakdown v1 Backfill card */}
        <Card className="bg-slate-900/40 border-slate-800 p-5 mb-4" data-testid="fee-breakdown-v1-backfill-card">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <h2 className="text-lg font-semibold text-violet-300">Fee-breakdown v1 backfill</h2>
              <p className="text-xs text-slate-400 mt-1 max-w-2xl">
                Rewrites every <code className="text-amber-300">payment_transactions.fee_breakdown</code> into the canonical v1 schema (line_items[] as source of truth, total = sum(line_items), source string, audit stamps). <strong>Idempotent.</strong> Never touches amount, status, payment_status, payment_reference, or registration_id. Each migrated row stamped with <code>_fb_backfilled_from_shape</code>, <code>_fb_backfilled_at</code>, and <code>_fb_legacy_keys</code> for forensic rollback.
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={loadBfPreview} disabled={bfLoading || bfCommitting} className="bg-violet-600 hover:bg-violet-500 text-white disabled:opacity-40" size="sm" data-testid="bf-preview-btn">
                {bfLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Database className="w-4 h-4 mr-1" />}
                Run preview
              </Button>
              {bf?.confirm_token && (
                <Button onClick={runBfCommit} disabled={bfCommitting} className="bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40" size="sm" data-testid="bf-commit-btn">
                  {bfCommitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
                  Commit backfill
                </Button>
              )}
            </div>
          </div>

          {bfLoading && !bf ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Scanning…</div>
          ) : bfResult ? (
            <div className="border border-emerald-700/40 bg-emerald-900/10 rounded p-4 space-y-2" data-testid="bf-result-panel">
              <div className="flex items-center gap-2 text-emerald-300 text-sm font-semibold"><ShieldCheck className="w-4 h-4" /> Backfill complete</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Converted legacy" value={bfResult.converted_legacy} accent="emerald" />
                <Stat label="Initialised null" value={bfResult.initialised_null} accent="violet" />
                <Stat label="Skipped (already v1)" value={bfResult.skipped_already_v1} accent="slate" />
                <Stat label="Errors" value={bfResult.errors?.length || 0} accent={(bfResult.errors?.length || 0) > 0 ? "rose" : "slate"} />
              </div>
              {bfResult.errors?.length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs text-rose-300 cursor-pointer">View errors ({bfResult.errors.length})</summary>
                  <pre className="text-[10px] bg-slate-950/80 border border-rose-900/40 rounded p-2 mt-2 max-h-40 overflow-auto">{JSON.stringify(bfResult.errors, null, 2)}</pre>
                </details>
              )}
              <div className="text-[10px] text-slate-500 italic">Completed at: <span className="font-mono">{bfResult.completed_at}</span></div>
            </div>
          ) : bf ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Stat label="Total txns" value={bf.total_payment_transactions} accent="slate" />
                <Stat label="Already v1" value={bf.already_v1_count} accent="emerald" />
                <Stat label="Legacy → v1" value={bf.legacy_to_convert_count} accent="violet" />
                <Stat label="Null → v1" value={bf.null_to_init_count} accent="amber" />
              </div>
              {bf.confirm_token && (bf.legacy_to_convert_count + bf.null_to_init_count) > 0 && (
                <div className="border border-violet-500/40 bg-violet-900/20 rounded p-3 mb-3 text-xs text-violet-200">
                  Confirm token issued (TTL {Math.round(bf.confirm_token_ttl_seconds / 60)} min). Click &quot;Commit backfill&quot; to migrate <strong>{bf.legacy_to_convert_count + bf.null_to_init_count}</strong> row(s).
                </div>
              )}
              {bf.legacy_to_convert_count + bf.null_to_init_count === 0 && (
                <div className="border border-emerald-700/40 bg-emerald-900/10 rounded p-3 text-xs text-emerald-300">
                  Nothing to migrate — all {bf.total_payment_transactions} rows are already at schema_version v1.
                </div>
              )}
              {bf.per_shape?.length > 0 && (
                <>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 mt-3 mb-2">Per-shape preview (legacy only — nulls counted above)</div>
                  <div className="space-y-2 max-h-[360px] overflow-y-auto">
                    {bf.per_shape.map((s, i) => {
                      const isOpen = bfOpenShape === i;
                      return (
                        <div key={i} className="border border-slate-800 rounded bg-slate-900/30" data-testid={`bf-shape-${i}`}>
                          <button type="button" onClick={() => setBfOpenShape(isOpen ? null : i)} className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-slate-900/50">
                            <div className="flex items-center gap-2 min-w-0">
                              {isOpen ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}
                              <Badge variant="outline" className="text-[10px] border-violet-500/50 text-violet-300">{s.rows} rows</Badge>
                              <span className="text-xs text-slate-400">{s.key_count} keys</span>
                              <span className="text-[10px] text-slate-500 truncate">sample pt: {s.sample_payment_type ?? "(null)"} · amount: {s.sample_amount}</span>
                            </div>
                          </button>
                          {isOpen && (
                            <div className="px-3 pb-2 pt-1 border-t border-slate-800">
                              <div className="flex flex-wrap gap-1">
                                {s.keys.map((k) => (<code key={k} className="text-[10px] bg-slate-800/70 border border-slate-700 text-slate-200 px-1.5 py-0.5 rounded">{k}</code>))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="text-slate-500 text-sm italic" data-testid="bf-empty-state">Click &quot;Run preview&quot; to scan payment_transactions for legacy / null fee_breakdown rows.</div>
          )}
        </Card>

        {/* Status field audit card */}
        <Card className="bg-slate-900/40 border-slate-800 p-5 mb-4" data-testid="status-field-audit-card">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <h2 className="text-lg font-semibold text-amber-300">Status field audit</h2>
              <p className="text-xs text-slate-400 mt-1 max-w-2xl">
                Read-only diagnostic. Reports the distribution of <code className="text-amber-300">status</code> vs <code className="text-amber-300">payment_status</code> across the whole <code>payment_transactions</code> collection, including every (status, payment_status) tuple and which payment_types use it. Used to plan the field-collapse migration in Step 3.
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={runStatusFieldAudit} disabled={sfaLoading} className="bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-40" size="sm" data-testid="run-status-audit-btn">
                {sfaLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
                Run status audit
              </Button>
              {sfa && !sfa.error && (
                <Button onClick={copyStatusAuditJson} variant="outline" size="sm" className="border-slate-700" data-testid="copy-status-audit-json-btn">Copy JSON</Button>
              )}
            </div>
          </div>

          {sfaLoading && !sfa ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Scanning…</div>
          ) : sfa?.error ? (
            <div className="text-rose-300 text-sm" data-testid="sfa-error">Error: {String(sfa.error)}</div>
          ) : sfa ? (
            <>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <Stat label="Total txns" value={sfa.totals?.total_payment_transactions} accent="slate" />
                <Stat label="Agree" value={sfa.totals?.agree_count} accent="emerald" />
                <Stat label="Disagree" value={sfa.totals?.disagree_count} accent="amber" />
              </div>

              <div className="grid md:grid-cols-2 gap-3 mb-4">
                <div className="border border-slate-800 rounded overflow-hidden">
                  <div className="bg-slate-900/70 px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400">`status` distribution</div>
                  <table className="w-full text-xs">
                    <tbody>
                      {(sfa.status_field_distribution || []).map((r, i) => (
                        <tr key={i} className="border-t border-slate-800">
                          <td className="px-2 py-1 font-mono text-slate-200">{r.value === null ? <span className="text-slate-500 italic">(null)</span> : r.value}</td>
                          <td className="px-2 py-1 text-right text-slate-300">{r.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="border border-slate-800 rounded overflow-hidden">
                  <div className="bg-slate-900/70 px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400">`payment_status` distribution</div>
                  <table className="w-full text-xs">
                    <tbody>
                      {(sfa.payment_status_field_distribution || []).map((r, i) => (
                        <tr key={i} className="border-t border-slate-800">
                          <td className="px-2 py-1 font-mono text-slate-200">{r.value === null ? <span className="text-slate-500 italic">(null)</span> : r.value}</td>
                          <td className="px-2 py-1 text-right text-slate-300">{r.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="border border-slate-800 rounded overflow-hidden mb-4">
                <div className="bg-slate-900/70 px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400">All (status, payment_status) pairs</div>
                <table className="w-full text-xs">
                  <thead className="bg-slate-900/40 text-slate-500 text-[10px]">
                    <tr><th className="px-2 py-1 text-left">status</th><th className="px-2 py-1 text-left">payment_status</th><th className="px-2 py-1 text-right">count</th><th className="px-2 py-1 text-left">payment_types</th></tr>
                  </thead>
                  <tbody>
                    {(sfa.pair_distribution || []).map((p, i) => (
                      <tr key={i} className="border-t border-slate-800">
                        <td className="px-2 py-1 font-mono text-slate-200">{p.status === null ? <span className="text-slate-500 italic">(null)</span> : p.status}</td>
                        <td className="px-2 py-1 font-mono text-slate-200">{p.payment_status === null ? <span className="text-slate-500 italic">(null)</span> : p.payment_status}</td>
                        <td className="px-2 py-1 text-right text-slate-300">{p.count}</td>
                        <td className="px-2 py-1 text-[10px] text-slate-500">{(p.payment_types || []).join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {(sfa.disagreement_patterns || []).length > 0 && (
                <>
                  <div className="text-[11px] uppercase tracking-wide text-amber-300 mb-1">Semantic disagreement (bucketed)</div>
                  <div className="space-y-1">
                    {sfa.disagreement_patterns.map((dp, i) => (
                      <div key={i} className="border border-amber-900/30 bg-amber-900/10 rounded px-3 py-2 text-xs" data-testid={`sfa-disagreement-${i}`}>
                        <div className="flex items-center justify-between">
                          <code className="text-amber-200">{dp.status_bucket} → {dp.payment_status_bucket}</code>
                          <span className="text-slate-300">{dp.count} rows</span>
                        </div>
                        <div className="mt-1 text-[10px] text-slate-400 space-y-0.5">
                          {(dp.raw_examples || []).slice(0, 2).map((ex, j) => (
                            <div key={j} className="font-mono">status={JSON.stringify(ex.status)}  ·  payment_status={JSON.stringify(ex.payment_status)}  ·  payment_type={JSON.stringify(ex.payment_type)}</div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className="mt-3 text-[10px] text-slate-500 italic">
                Generated at: <span className="font-mono">{sfa.generated_at}</span> · audit_version: <span className="font-mono">{sfa.audit_version}</span>
              </div>
            </>
          ) : (
            <div className="text-slate-500 text-sm italic" data-testid="sfa-empty-state">Click &quot;Run status audit&quot; to scan payment_transactions.</div>
          )}
        </Card>

        {/* Status collapse migration card */}
        <Card className="bg-slate-900/40 border-slate-800 p-5 mb-4" data-testid="status-collapse-card">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <h2 className="text-lg font-semibold text-rose-300">Status field collapse</h2>
              <p className="text-xs text-slate-400 mt-1 max-w-2xl">
                Collapses <code className="text-amber-300">status</code> + <code className="text-amber-300">payment_status</code> into a single canonical <code className="text-emerald-300">payment_status</code> field. Renames the legacy <code>status</code> field → <code>_legacy_status</code> (preserved for 14 days — NOT deleted by this migration). Stamps <code>_status_collapsed_from</code> + <code>_status_collapsed_at</code> on every row.
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={loadScPreview} disabled={scLoading || scCommitting} className="bg-rose-600 hover:bg-rose-500 text-white disabled:opacity-40" size="sm" data-testid="sc-preview-btn">
                {scLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Database className="w-4 h-4 mr-1" />}
                Run preview
              </Button>
              {sc?.confirm_token && (
                <Button onClick={runScCommit} disabled={scCommitting} className="bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40" size="sm" data-testid="sc-commit-btn">
                  {scCommitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
                  Commit collapse
                </Button>
              )}
            </div>
          </div>

          {scLoading && !sc ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Scanning…</div>
          ) : scResult ? (
            <div className="border border-emerald-700/40 bg-emerald-900/10 rounded p-4 space-y-2" data-testid="sc-result-panel">
              <div className="flex items-center gap-2 text-emerald-300 text-sm font-semibold"><ShieldCheck className="w-4 h-4" /> Status collapse complete</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Collapsed" value={scResult.collapsed_count} accent="emerald" />
                <Stat label="Fallback resolved" value={scResult.fallback_resolved_count} accent="amber" />
                <Stat label="Skipped (already)" value={scResult.skipped_already_collapsed} accent="slate" />
                <Stat label="Errors" value={scResult.errors?.length || 0} accent={(scResult.errors?.length || 0) > 0 ? "rose" : "slate"} />
              </div>
              {scResult.errors?.length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs text-rose-300 cursor-pointer">View errors ({scResult.errors.length})</summary>
                  <pre className="text-[10px] bg-slate-950/80 border border-rose-900/40 rounded p-2 mt-2 max-h-40 overflow-auto">{JSON.stringify(scResult.errors, null, 2)}</pre>
                </details>
              )}
              <div className="text-[10px] text-slate-500 italic">Completed at: <span className="font-mono">{scResult.completed_at}</span></div>
            </div>
          ) : sc ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Stat label="Total txns" value={sc.total_payment_transactions} accent="slate" />
                <Stat label="Already collapsed" value={sc.already_collapsed_count} accent="emerald" />
                <Stat label="To collapse" value={sc.to_collapse_count} accent="rose" />
                <Stat label="Unknown pairs" value={sc.unknown_pairs?.length || 0} accent={(sc.unknown_pairs?.length || 0) > 0 ? "amber" : "slate"} />
              </div>

              {sc.confirm_token && sc.to_collapse_count > 0 && (
                <div className="border border-rose-500/40 bg-rose-900/20 rounded p-3 mb-3 text-xs text-rose-200">
                  Confirm token issued (TTL {Math.round(sc.confirm_token_ttl_seconds / 60)} min). Click &quot;Commit collapse&quot; to migrate <strong>{sc.to_collapse_count}</strong> row(s).
                </div>
              )}
              {sc.to_collapse_count === 0 && (
                <div className="border border-emerald-700/40 bg-emerald-900/10 rounded p-3 text-xs text-emerald-300">
                  Nothing to collapse — all {sc.total_payment_transactions} rows are already canonical.
                </div>
              )}

              {sc.per_rule?.length > 0 && (
                <>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Per-rule preview</div>
                  <div className="border border-slate-800 rounded overflow-hidden mb-3">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-900/70 text-slate-500 text-[10px]">
                        <tr><th className="px-2 py-1 text-left">status</th><th className="px-2 py-1 text-left">payment_status</th><th className="px-2 py-1 text-right">rows</th><th className="px-2 py-1 text-left">new payment_status</th></tr>
                      </thead>
                      <tbody>
                        {sc.per_rule.map((r, i) => (
                          <tr key={i} className="border-t border-slate-800">
                            <td className="px-2 py-1 font-mono text-slate-200">{r.status === null ? <span className="text-slate-500 italic">(null)</span> : r.status}</td>
                            <td className="px-2 py-1 font-mono text-slate-200">{r.payment_status === null ? <span className="text-slate-500 italic">(null)</span> : r.payment_status}</td>
                            <td className="px-2 py-1 text-right text-slate-300">{r.rows}</td>
                            <td className="px-2 py-1 font-mono text-emerald-300">{r.new_payment_status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {sc.unknown_pairs?.length > 0 && (
                <>
                  <div className="text-[11px] uppercase tracking-wide text-amber-300 mb-2">Unknown pairs (fallback to payment_status verbatim)</div>
                  <div className="space-y-1">
                    {sc.unknown_pairs.map((u, i) => (
                      <div key={i} className="border border-amber-900/30 bg-amber-900/10 rounded px-3 py-2 text-xs">
                        <div className="flex items-center justify-between">
                          <code className="text-amber-200">status={JSON.stringify(u.status)} · payment_status={JSON.stringify(u.payment_status)}</code>
                          <span className="text-slate-300">{u.count} rows → {u.would_resolve_to}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="text-slate-500 text-sm italic" data-testid="sc-empty-state">Click &quot;Run preview&quot; to scan payment_transactions for status-collapse candidates.</div>
          )}
        </Card>

        {/* Registration link audit card */}
        <Card className="bg-slate-900/40 border-slate-800 p-5 mb-4" data-testid="registration-link-audit-card">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <h2 className="text-lg font-semibold text-cyan-300">Registration link audit</h2>
              <p className="text-xs text-slate-400 mt-1 max-w-2xl">
                Read-only diagnostic. Walks <code className="text-amber-300">payment_transactions</code> and reports rows whose <code>registration_id</code> is missing or points at a non-existent registration. For each orphan, computes whether it can be auto-linked by matching <code>(parent_id, term_id, amount)</code> against <code>academy_parent_registrations</code>.
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={runRegistrationLinkAudit} disabled={rlaLoading} className="bg-cyan-600 hover:bg-cyan-500 text-white disabled:opacity-40" size="sm" data-testid="run-rla-btn">
                {rlaLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
                Run audit
              </Button>
              {rla && !rla.error && (
                <Button onClick={copyRlaJson} variant="outline" size="sm" className="border-slate-700" data-testid="copy-rla-btn">Copy JSON</Button>
              )}
            </div>
          </div>

          {rlaLoading && !rla ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Scanning…</div>
          ) : rla?.error ? (
            <div className="text-rose-300 text-sm" data-testid="rla-error">Error: {String(rla.error)}</div>
          ) : rla ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                <Stat label="Total txns" value={rla.totals?.total_payment_transactions} accent="slate" />
                <Stat label="Linked & valid" value={rla.totals?.registration_id_set_and_valid} accent="emerald" />
                <Stat label="Null reg_id" value={rla.totals?.registration_id_null} accent="amber" />
                <Stat label="Stale reg_id" value={rla.totals?.registration_id_stale} accent="rose" />
                <Stat label="Auto-linkable" value={rla.totals?.auto_linkable_count} accent="blue" />
                <Stat label="Unlinkable" value={rla.totals?.unlinkable_count} accent="slate" />
              </div>
              {(rla.totals?.muted_legacy_count > 0 || rla.totals?.muted_stale_count > 0) && (
                <div className="border border-slate-700/60 bg-slate-900/60 rounded p-2 mb-4 text-[11px] text-slate-400">
                  Muted (tagged by auto-backfill, excluded from above counts):
                  <span className="ml-2 text-slate-300">legacy = <strong>{rla.totals.muted_legacy_count}</strong></span>
                  <span className="ml-3 text-slate-300">stale = <strong>{rla.totals.muted_stale_count}</strong></span>
                </div>
              )}

              <div className="border border-slate-800 rounded overflow-hidden mb-4">
                <div className="bg-slate-900/70 px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400">Per payment_type breakdown</div>
                <table className="w-full text-xs">
                  <thead className="bg-slate-900/40 text-slate-500 text-[10px]">
                    <tr><th className="px-2 py-1 text-left">payment_type</th><th className="px-2 py-1 text-right">orphan</th><th className="px-2 py-1 text-right">auto-linkable</th></tr>
                  </thead>
                  <tbody>
                    {(rla.per_payment_type_breakdown || []).map((r, i) => (
                      <tr key={i} className="border-t border-slate-800">
                        <td className="px-2 py-1 font-mono text-slate-200">{r.payment_type}</td>
                        <td className="px-2 py-1 text-right text-amber-300">{r.orphan_count}</td>
                        <td className="px-2 py-1 text-right text-blue-300">{r.auto_linkable_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Orphan / Stale tabs */}
              <div className="flex gap-1 mb-2 border-b border-slate-800">
                <button onClick={() => setRlaTab("orphan")} className={`text-xs px-3 py-1 ${rlaTab === "orphan" ? "border-b-2 border-cyan-400 text-cyan-300" : "text-slate-400"}`} data-testid="rla-tab-orphan">Null reg_id ({(rla.orphan_rows || []).length})</button>
                <button onClick={() => setRlaTab("stale")} className={`text-xs px-3 py-1 ${rlaTab === "stale" ? "border-b-2 border-rose-400 text-rose-300" : "text-slate-400"}`} data-testid="rla-tab-stale">Stale reg_id ({(rla.stale_rows || []).length})</button>
              </div>
              <div className="border border-slate-800 rounded overflow-hidden max-h-[420px] overflow-y-auto">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-900/70 text-slate-500 text-[10px] sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left">txn_id</th>
                      <th className="px-2 py-1 text-left">payment_type</th>
                      <th className="px-2 py-1 text-left">parent</th>
                      <th className="px-2 py-1 text-right">amount</th>
                      <th className="px-2 py-1 text-left">created</th>
                      <th className="px-2 py-1 text-center">candidates</th>
                    </tr>
                  </thead>
                  <tbody>
                    {((rlaTab === "orphan" ? rla.orphan_rows : rla.stale_rows) || []).map((r, i) => {
                      const c = (r.auto_link_candidates || []).length;
                      return (
                        <tr key={i} className="border-t border-slate-800" data-testid={`rla-${rlaTab}-row-${i}`}>
                          <td className="px-2 py-1 font-mono text-slate-300">{(r.txn_id || "").slice(0, 8)}</td>
                          <td className="px-2 py-1 font-mono text-slate-300">{r.payment_type ?? <span className="text-slate-500 italic">(null)</span>}</td>
                          <td className="px-2 py-1 text-slate-300 truncate max-w-[200px]" title={r.parent_email || ""}>{r.parent_name || r.parent_email || "—"}</td>
                          <td className="px-2 py-1 text-right text-slate-200">{r.amount} <span className="text-slate-500">{r.currency?.toUpperCase()}</span></td>
                          <td className="px-2 py-1 text-[10px] text-slate-500">{(r.created_at || "").slice(0, 10)}</td>
                          <td className={`px-2 py-1 text-center font-mono ${c === 1 ? "text-emerald-300" : c >= 2 ? "text-amber-300" : "text-slate-500"}`}>{c}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 text-[10px] text-slate-500">
                <span className="text-emerald-400">●</span> 1 candidate = safe to auto-link &nbsp;·&nbsp;
                <span className="text-amber-400">●</span> 2+ candidates = needs disambiguation &nbsp;·&nbsp;
                <span className="text-slate-500">●</span> 0 candidates = unlinkable
              </div>

              <div className="mt-3 text-[10px] text-slate-500 italic">
                Generated at: <span className="font-mono">{rla.generated_at}</span> · audit_version: <span className="font-mono">{rla.audit_version}</span>
              </div>
            </>
          ) : (
            <div className="text-slate-500 text-sm italic" data-testid="rla-empty-state">Click &quot;Run audit&quot; to scan payment_transactions for missing or stale registration links.</div>
          )}
        </Card>

        {/* Registration link auto-backfill card */}
        <Card className="bg-slate-900/40 border-slate-800 p-5 mb-4" data-testid="registration-link-backfill-card">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <h2 className="text-lg font-semibold text-blue-300">Registration link auto-backfill</h2>
              <p className="text-xs text-slate-400 mt-1 max-w-2xl">
                Resolves the orphans flagged by the audit above. <strong className="text-emerald-300">Auto-links</strong> rows with exactly 1 candidate, <strong className="text-amber-300">skips</strong> ambiguous rows (2+ candidates) for manual review, marks <strong className="text-slate-300">unlinkable</strong> rows as <code>_registration_link_legacy</code>, and marks <strong className="text-rose-300">stale</strong> rows as <code>_registration_link_stale</code>. Every mutated row carries <code>_registration_linked_at</code> + <code>_registration_linked_from</code>=&quot;auto_backfill&quot;.
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={loadRlbPreview} disabled={rlbLoading || rlbCommitting} className="bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40" size="sm" data-testid="rlb-preview-btn">
                {rlbLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Database className="w-4 h-4 mr-1" />}
                Run preview
              </Button>
              {rlb?.confirm_token && (
                <Button onClick={runRlbCommit} disabled={rlbCommitting} className="bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40" size="sm" data-testid="rlb-commit-btn">
                  {rlbCommitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
                  Commit backfill
                </Button>
              )}
            </div>
          </div>

          {rlbLoading && !rlb ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Scanning…</div>
          ) : rlbResult ? (
            <div className="border border-emerald-700/40 bg-emerald-900/10 rounded p-4 space-y-2" data-testid="rlb-result-panel">
              <div className="flex items-center gap-2 text-emerald-300 text-sm font-semibold"><ShieldCheck className="w-4 h-4" /> Registration-link backfill complete</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Auto-linked" value={rlbResult.auto_linked_count} accent="emerald" />
                <Stat label="Legacy marked" value={rlbResult.legacy_marked_count} accent="slate" />
                <Stat label="Stale marked" value={rlbResult.stale_marked_count} accent="rose" />
                <Stat label="Ambiguous skipped" value={rlbResult.ambiguous_skipped_count} accent="amber" />
              </div>
              {rlbResult.errors?.length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs text-rose-300 cursor-pointer">View errors ({rlbResult.errors.length})</summary>
                  <pre className="text-[10px] bg-slate-950/80 border border-rose-900/40 rounded p-2 mt-2 max-h-40 overflow-auto">{JSON.stringify(rlbResult.errors, null, 2)}</pre>
                </details>
              )}
              <div className="text-[10px] text-slate-500 italic">Completed at: <span className="font-mono">{rlbResult.completed_at}</span></div>
            </div>
          ) : rlb ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                <Stat label="Total txns" value={rlb.total_payment_transactions} accent="slate" />
                <Stat label="Already done" value={rlb.already_processed_count} accent="emerald" />
                <Stat label="Auto-link" value={rlb.auto_link_count} accent="blue" />
                <Stat label="Ambiguous" value={rlb.ambiguous_count} accent="amber" />
                <Stat label="Legacy" value={rlb.legacy_count} accent="slate" />
                <Stat label="Stale" value={rlb.stale_count} accent="rose" />
              </div>

              {rlb.confirm_token && rlb.to_mutate_count > 0 && (
                <div className="border border-blue-500/40 bg-blue-900/20 rounded p-3 mb-3 text-xs text-blue-200" data-testid="rlb-token-banner">
                  Confirm token issued (TTL {Math.round(rlb.confirm_token_ttl_seconds / 60)} min). Click &quot;Commit backfill&quot; to mutate <strong>{rlb.to_mutate_count}</strong> row(s) — {rlb.auto_link_count} link · {rlb.legacy_count} legacy · {rlb.stale_count} stale. {rlb.ambiguous_count} ambiguous row(s) will be skipped.
                </div>
              )}
              {rlb.to_mutate_count === 0 && (
                <div className="border border-emerald-700/40 bg-emerald-900/10 rounded p-3 text-xs text-emerald-300">
                  Nothing to backfill — all orphan rows are already processed or ambiguous.
                </div>
              )}

              {/* Per-bucket tabs */}
              <div className="flex gap-1 mb-2 border-b border-slate-800 flex-wrap">
                <button onClick={() => setRlbTab("auto_link")} className={`text-xs px-3 py-1 ${rlbTab === "auto_link" ? "border-b-2 border-emerald-400 text-emerald-300" : "text-slate-400"}`} data-testid="rlb-tab-auto">Auto-link ({rlb.auto_link_count})</button>
                <button onClick={() => setRlbTab("ambiguous")} className={`text-xs px-3 py-1 ${rlbTab === "ambiguous" ? "border-b-2 border-amber-400 text-amber-300" : "text-slate-400"}`} data-testid="rlb-tab-ambiguous">Ambiguous ({rlb.ambiguous_count})</button>
                <button onClick={() => setRlbTab("legacy")} className={`text-xs px-3 py-1 ${rlbTab === "legacy" ? "border-b-2 border-slate-300 text-slate-200" : "text-slate-400"}`} data-testid="rlb-tab-legacy">Legacy ({rlb.legacy_count})</button>
                <button onClick={() => setRlbTab("stale")} className={`text-xs px-3 py-1 ${rlbTab === "stale" ? "border-b-2 border-rose-400 text-rose-300" : "text-slate-400"}`} data-testid="rlb-tab-stale">Stale ({rlb.stale_count})</button>
              </div>
              <div className="border border-slate-800 rounded overflow-hidden max-h-[420px] overflow-y-auto">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-900/70 text-slate-500 text-[10px] sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left">txn_id</th>
                      <th className="px-2 py-1 text-left">payment_type</th>
                      <th className="px-2 py-1 text-left">parent</th>
                      <th className="px-2 py-1 text-right">amount</th>
                      <th className="px-2 py-1 text-left">created</th>
                      <th className="px-2 py-1 text-left">{rlbTab === "auto_link" ? "chosen reg_id" : rlbTab === "stale" ? "broken reg_id" : "candidates"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(rlb[`${rlbTab}_rows`] || []).map((r, i) => (
                      <tr key={i} className="border-t border-slate-800" data-testid={`rlb-${rlbTab}-row-${i}`}>
                        <td className="px-2 py-1 font-mono text-slate-300">{(r.txn_id || "").slice(0, 8)}</td>
                        <td className="px-2 py-1 font-mono text-slate-300">{r.payment_type ?? <span className="text-slate-500 italic">(null)</span>}</td>
                        <td className="px-2 py-1 text-slate-300 truncate max-w-[200px]" title={r.parent_email || ""}>{r.parent_name || r.parent_email || "—"}</td>
                        <td className="px-2 py-1 text-right text-slate-200">{r.amount} <span className="text-slate-500">{r.currency?.toUpperCase()}</span></td>
                        <td className="px-2 py-1 text-[10px] text-slate-500">{(r.created_at || "").slice(0, 10)}</td>
                        <td className="px-2 py-1 font-mono text-[10px]">
                          {rlbTab === "auto_link" ? (
                            <span className="text-emerald-300">{(r.chosen_registration_id || "").slice(0, 12)}…</span>
                          ) : rlbTab === "stale" ? (
                            <span className="text-rose-300">{(r.broken_registration_id || "").slice(0, 12)}…</span>
                          ) : rlbTab === "ambiguous" ? (
                            <span className="text-amber-300">{(r.candidates || []).length} matches</span>
                          ) : (
                            <span className="text-slate-500">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {(rlb[`${rlbTab}_rows`] || []).length === 0 && (
                      <tr><td colSpan={6} className="px-2 py-4 text-center text-slate-500 italic">No rows in this bucket.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 text-[10px] text-slate-500 italic">
                Generated at: <span className="font-mono">{rlb.generated_at}</span> · migration: <span className="font-mono">{rlb.migration_id}</span>
              </div>
            </>
          ) : (
            <div className="text-slate-500 text-sm italic" data-testid="rlb-empty-state">Click &quot;Run preview&quot; to scan for backfillable registration links.</div>
          )}
        </Card>

        {/* Canonical amount audit card */}
        <Card className="bg-slate-900/40 border-slate-800 p-5 mb-4" data-testid="canonical-amount-audit-card">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <h2 className="text-lg font-semibold text-fuchsia-300">Canonical amount audit</h2>
              <p className="text-xs text-slate-400 mt-1 max-w-2xl">
                Read-only diagnostic. Scans <code className="text-amber-300">payment_transactions</code> for every amount-like field (<code>amount</code>, <code>amount_invoiced</code>, <code>amount_paid</code>, <code>amount_received</code>, <code>received_amount</code>, <code>expected_amount</code>, <code>total_fee</code>, <code>fee_breakdown.total</code>), counts agreements vs disagreements against <code>amount</code>, and emits a recommendation for the single source-of-truth field. No mutations.
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={runCaaAudit} disabled={caaLoading} className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white disabled:opacity-40" size="sm" data-testid="run-caa-btn">
                {caaLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
                Run audit
              </Button>
              {caa && !caa.error && (
                <Button onClick={copyCaaJson} variant="outline" size="sm" className="border-slate-700" data-testid="copy-caa-btn">Copy JSON</Button>
              )}
            </div>
          </div>

          {caaLoading && !caa ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Scanning…</div>
          ) : caa?.error ? (
            <div className="text-rose-300 text-sm" data-testid="caa-error">Error: {String(caa.error)}</div>
          ) : caa ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Stat label="Total txns" value={caa.totals?.total_payment_transactions} accent="slate" />
                <Stat label="Universal fields" value={(caa.universal_fields || []).length} accent="emerald" />
                <Stat label="Sparse fields" value={(caa.sparse_fields || []).length} accent="amber" />
                <Stat label="Coverage combos" value={(caa.coverage_combinations || []).length} accent="blue" />
              </div>

              {/* Recommendation block */}
              <div className="border border-fuchsia-500/40 bg-fuchsia-900/15 rounded p-3 mb-4">
                <div className="flex items-center gap-2 text-fuchsia-200 text-sm font-semibold mb-2">
                  <ShieldCheck className="w-4 h-4" /> Recommendation
                </div>
                <div className="text-xs text-slate-200 mb-2">
                  Keep <code className="text-emerald-300">{caa.recommendation?.canonical_field}</code> as canonical.
                  {caa.recommendation?.keep_alongside?.length > 0 && (
                    <>{" "}Retain alongside: {caa.recommendation.keep_alongside.map(f => <code key={f} className="text-emerald-300 ml-1">{f}</code>)}.</>
                  )}
                  {caa.recommendation?.retire_candidates?.length > 0 && (
                    <>{" "}Retire: {caa.recommendation.retire_candidates.map((f, i) => <code key={f} className="text-amber-300 ml-1">{f}{i < caa.recommendation.retire_candidates.length - 1 ? "," : ""}</code>)}.</>
                  )}
                </div>
                <ul className="text-[11px] text-slate-300 space-y-1 list-disc list-inside">
                  {(caa.recommendation?.notes || []).map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              </div>

              {/* Field presence table */}
              <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Field presence</div>
              <div className="border border-slate-800 rounded overflow-hidden mb-4">
                <table className="w-full text-xs">
                  <thead className="bg-slate-900/70 text-slate-500 text-[10px]">
                    <tr>
                      <th className="px-2 py-1 text-left">field</th>
                      <th className="px-2 py-1 text-right">rows</th>
                      <th className="px-2 py-1 text-right">%</th>
                      <th className="px-2 py-1 text-left">classification</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(caa.field_presence || []).map((f, i) => {
                      const pct = f.pct;
                      const cls = pct === 100 ? "universal" : pct === 0 ? "absent" : "sparse";
                      const clsColor = cls === "universal" ? "text-emerald-300" : cls === "absent" ? "text-slate-500" : "text-amber-300";
                      return (
                        <tr key={i} className="border-t border-slate-800">
                          <td className="px-2 py-1 font-mono text-slate-200">{f.field}</td>
                          <td className="px-2 py-1 text-right text-slate-300">{f.rows}</td>
                          <td className="px-2 py-1 text-right text-slate-400">{pct.toFixed(2)}%</td>
                          <td className={`px-2 py-1 ${clsColor}`}>{cls}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Agreement vs amount */}
              <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Agreement vs <code>amount</code></div>
              <div className="border border-slate-800 rounded overflow-hidden mb-4">
                <table className="w-full text-xs">
                  <thead className="bg-slate-900/70 text-slate-500 text-[10px]">
                    <tr>
                      <th className="px-2 py-1 text-left">field</th>
                      <th className="px-2 py-1 text-right">present</th>
                      <th className="px-2 py-1 text-right">agree</th>
                      <th className="px-2 py-1 text-right">disagree</th>
                      <th className="px-2 py-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(caa.agreement_with_amount || []).map((a, i) => {
                      const open = caaOpenField === a.field;
                      const disagree = a.disagree;
                      const hasSamples = (a.samples || []).length > 0;
                      return (
                        <React.Fragment key={i}>
                          <tr className="border-t border-slate-800">
                            <td className="px-2 py-1 font-mono text-slate-200">{a.field}</td>
                            <td className="px-2 py-1 text-right text-slate-300">{a.present}</td>
                            <td className="px-2 py-1 text-right text-emerald-300">{a.agree}</td>
                            <td className={`px-2 py-1 text-right ${disagree > 0 ? "text-rose-300" : "text-slate-500"}`}>{disagree}</td>
                            <td className="px-2 py-1">
                              {hasSamples && (
                                <button onClick={() => setCaaOpenField(open ? null : a.field)} className="text-[10px] text-slate-400 hover:text-slate-200 underline" data-testid={`caa-toggle-${a.field}`}>
                                  {open ? "hide" : `show ${a.samples.length} sample(s)`}
                                </button>
                              )}
                            </td>
                          </tr>
                          {open && hasSamples && (
                            <tr className="border-t border-slate-900 bg-slate-950/60">
                              <td colSpan={5} className="p-2">
                                <pre className="text-[10px] text-slate-300 bg-slate-950/80 border border-slate-800 rounded p-2 max-h-64 overflow-auto">{JSON.stringify(a.samples, null, 2)}</pre>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Coverage combinations */}
              <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Coverage combinations</div>
              <div className="border border-slate-800 rounded overflow-hidden mb-3 max-h-[320px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-900/70 text-slate-500 text-[10px] sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left">fields set together</th>
                      <th className="px-2 py-1 text-right">rows</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(caa.coverage_combinations || []).map((c, i) => (
                      <tr key={i} className="border-t border-slate-800">
                        <td className="px-2 py-1 text-slate-200">
                          {c.fields.map(f => <code key={f} className="font-mono text-[10px] mr-1 text-slate-300">{f}</code>)}
                        </td>
                        <td className="px-2 py-1 text-right text-slate-300">{c.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 text-[10px] text-slate-500 italic">
                Generated at: <span className="font-mono">{caa.generated_at}</span> · audit_version: <span className="font-mono">{caa.audit_version}</span>
              </div>
            </>
          ) : (
            <div className="text-slate-500 text-sm italic" data-testid="caa-empty-state">Click &quot;Run audit&quot; to scan payment_transactions for amount-field drift.</div>
          )}
        </Card>

        {/* Step 5 — Retire amount duplicates */}
        <Card className="bg-slate-900/40 border-slate-800 p-5 mb-4" data-testid="retire-amount-duplicates-card">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <h2 className="text-lg font-semibold text-violet-300">Retire amount duplicates</h2>
              <p className="text-xs text-slate-400 mt-1 max-w-2xl">
                Retires the two true-duplicate amount fields. Moves <code className="text-amber-300">received_amount</code> → <code className="text-slate-300">_legacy_received_amount</code> and <code className="text-amber-300">expected_amount</code> → <code className="text-slate-300">_legacy_expected_amount</code>. The canonical pair (<code className="text-emerald-300">amount</code>, <code className="text-emerald-300">amount_received</code>) is never touched. Idempotent.
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={loadRadPreview} disabled={radLoading || radCommitting} className="bg-violet-600 hover:bg-violet-500 text-white disabled:opacity-40" size="sm" data-testid="rad-preview-btn">
                {radLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Database className="w-4 h-4 mr-1" />}
                Run preview
              </Button>
              {rad?.confirm_token && (
                <Button onClick={runRadCommit} disabled={radCommitting} className="bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40" size="sm" data-testid="rad-commit-btn">
                  {radCommitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
                  Commit retire
                </Button>
              )}
            </div>
          </div>

          {radLoading && !rad ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Scanning…</div>
          ) : radResult ? (
            <div className="border border-emerald-700/40 bg-emerald-900/10 rounded p-4 space-y-2" data-testid="rad-result-panel">
              <div className="flex items-center gap-2 text-emerald-300 text-sm font-semibold"><ShieldCheck className="w-4 h-4" /> Retirement complete</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Rows mutated" value={radResult.rows_mutated} accent="emerald" />
                <Stat label="received_amount retired" value={radResult.retired_received_amount_count} accent="amber" />
                <Stat label="expected_amount retired" value={radResult.retired_expected_amount_count} accent="amber" />
                <Stat label="Errors" value={radResult.errors?.length || 0} accent={(radResult.errors?.length || 0) > 0 ? "rose" : "slate"} />
              </div>
              <div className="text-[10px] text-slate-500 italic">Completed at: <span className="font-mono">{radResult.completed_at}</span></div>
            </div>
          ) : rad ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Stat label="Total txns" value={rad.total_payment_transactions} accent="slate" />
                <Stat label="Already retired" value={rad.already_retired_count} accent="emerald" />
                <Stat label="To mutate" value={rad.to_mutate_count} accent="violet" />
                <Stat label="Both fields set" value={rad.has_both_count} accent="amber" />
              </div>
              {rad.confirm_token && rad.to_mutate_count > 0 && (
                <div className="border border-violet-500/40 bg-violet-900/20 rounded p-3 mb-3 text-xs text-violet-200" data-testid="rad-token-banner">
                  Confirm token issued (TTL {Math.round(rad.confirm_token_ttl_seconds / 60)} min). Click &quot;Commit retire&quot; to mutate <strong>{rad.to_mutate_count}</strong> row(s).
                </div>
              )}
              {rad.to_mutate_count === 0 && (
                <div className="border border-emerald-700/40 bg-emerald-900/10 rounded p-3 text-xs text-emerald-300">
                  Nothing to retire — all rows already collapsed.
                </div>
              )}

              {(rad.sample_rows || []).length > 0 && (
                <>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Sample rows ({rad.sample_rows.length} of {rad.to_mutate_count})</div>
                  <div className="border border-slate-800 rounded overflow-hidden max-h-[320px] overflow-y-auto">
                    <table className="w-full text-[11px]">
                      <thead className="bg-slate-900/70 text-slate-500 text-[10px] sticky top-0">
                        <tr>
                          <th className="px-2 py-1 text-left">txn_id</th>
                          <th className="px-2 py-1 text-left">parent</th>
                          <th className="px-2 py-1 text-right">amount</th>
                          <th className="px-2 py-1 text-right">amount_received</th>
                          <th className="px-2 py-1 text-right">received_amount</th>
                          <th className="px-2 py-1 text-right">expected_amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rad.sample_rows.map((r, i) => (
                          <tr key={i} className="border-t border-slate-800" data-testid={`rad-sample-row-${i}`}>
                            <td className="px-2 py-1 font-mono text-slate-300">{(r.txn_id || "").slice(0, 8)}</td>
                            <td className="px-2 py-1 text-slate-300 truncate max-w-[180px]" title={r.parent_email || ""}>{r.parent_email || "—"}</td>
                            <td className="px-2 py-1 text-right text-emerald-300">{r.amount ?? "—"}</td>
                            <td className="px-2 py-1 text-right text-emerald-300">{r.amount_received ?? "—"}</td>
                            <td className="px-2 py-1 text-right text-amber-300">{r.received_amount ?? "—"}</td>
                            <td className="px-2 py-1 text-right text-amber-300">{r.expected_amount ?? "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              <div className="mt-3 text-[10px] text-slate-500 italic">
                Generated at: <span className="font-mono">{rad.generated_at}</span> · migration: <span className="font-mono">{rad.migration_id}</span>
              </div>
            </>
          ) : (
            <div className="text-slate-500 text-sm italic" data-testid="rad-empty-state">Click &quot;Run preview&quot; to find rows still carrying duplicate amount fields.</div>
          )}
        </Card>

        {/* Step 6 — Scheduled cleanup (daily 02:00 SGT + manual trigger) */}
        <Card className="bg-slate-900/40 border-slate-800 p-5 mb-4" data-testid="scheduled-cleanup-card">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <h2 className="text-lg font-semibold text-teal-300">Scheduled cleanup</h2>
              <p className="text-xs text-slate-400 mt-1 max-w-2xl">
                Runs daily at <strong className="text-teal-300">02:00 SGT</strong>. Archives abandoned checkouts (<code>payment_status = &quot;initiated&quot;</code> older than 24h) and stale draft registrations (older than 14 days). Logs counts to <code>admin_audit_log</code>. Idempotent — manual triggering is safe.
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={runScheduledCleanup} disabled={sclRunning} className="bg-teal-600 hover:bg-teal-500 text-white disabled:opacity-40" size="sm" data-testid="scl-run-btn">
                {sclRunning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
                Run now
              </Button>
              <Button onClick={loadSclLastRuns} disabled={sclLastLoading} variant="outline" size="sm" className="border-slate-700" data-testid="scl-history-btn">
                {sclLastLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-1" />}
                Load history
              </Button>
            </div>
          </div>

          {sclResult && (
            <div className="border border-emerald-700/40 bg-emerald-900/10 rounded p-4 space-y-2 mb-4" data-testid="scl-result-panel">
              <div className="flex items-center gap-2 text-emerald-300 text-sm font-semibold"><ShieldCheck className="w-4 h-4" /> Cleanup complete</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Checkouts archived" value={sclResult.abandoned_checkouts_archived} accent="emerald" />
                <Stat label="Drafts archived" value={sclResult.stale_drafts_archived} accent="emerald" />
                <Stat label="Checkout errors" value={sclResult.abandoned_checkouts_errors} accent={sclResult.abandoned_checkouts_errors > 0 ? "rose" : "slate"} />
                <Stat label="Draft errors" value={sclResult.stale_drafts_errors} accent={sclResult.stale_drafts_errors > 0 ? "rose" : "slate"} />
              </div>
              <div className="text-[10px] text-slate-500 italic space-x-3">
                <span>Run by: <span className="font-mono text-slate-400">{sclResult.actor}</span></span>
                <span>Checkout cutoff: <span className="font-mono text-slate-400">{sclResult.abandoned_checkouts_cutoff}</span></span>
                <span>Draft cutoff: <span className="font-mono text-slate-400">{sclResult.stale_drafts_cutoff}</span></span>
              </div>
            </div>
          )}

          {sclLastRuns && sclLastRuns.length > 0 && (
            <>
              <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Recent runs (last {sclLastRuns.length})</div>
              <div className="border border-slate-800 rounded overflow-hidden max-h-[280px] overflow-y-auto">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-900/70 text-slate-500 text-[10px] sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left">timestamp</th>
                      <th className="px-2 py-1 text-left">actor</th>
                      <th className="px-2 py-1 text-right">checkouts</th>
                      <th className="px-2 py-1 text-right">drafts</th>
                      <th className="px-2 py-1 text-right">errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sclLastRuns.map((r, i) => {
                      const s = r.summary || {};
                      const errs = (s.abandoned_checkouts_errors || 0) + (s.stale_drafts_errors || 0);
                      return (
                        <tr key={i} className="border-t border-slate-800">
                          <td className="px-2 py-1 font-mono text-[10px] text-slate-300">{(r.timestamp || "").replace("T", " ").slice(0, 19)}</td>
                          <td className="px-2 py-1 text-slate-300 truncate max-w-[180px]" title={s.actor || ""}>{s.actor || "—"}</td>
                          <td className="px-2 py-1 text-right text-emerald-300">{s.abandoned_checkouts_archived ?? 0}</td>
                          <td className="px-2 py-1 text-right text-emerald-300">{s.stale_drafts_archived ?? 0}</td>
                          <td className={`px-2 py-1 text-right ${errs > 0 ? "text-rose-300" : "text-slate-500"}`}>{errs}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {!sclResult && !sclLastRuns && (
            <div className="text-slate-500 text-sm italic" data-testid="scl-empty-state">Click &quot;Run now&quot; to trigger the cleanup, or &quot;Load history&quot; to see the last 10 scheduled runs.</div>
          )}
        </Card>

        <div className="text-[10px] text-slate-600 italic">
          <AlertTriangle className="w-3 h-3 inline mr-1 text-amber-500" />
          Migrations are auditable. Each inserted row carries <code>_migrated_from</code> and <code>_migrated_at</code> so a rollback is a single Mongo delete on that marker.
        </div>

        {/* Historical one-shot migrations (merged from old /admin/migration-tools) */}
        <Card className="bg-slate-900/40 border-slate-800 p-5 mt-6" data-testid="oneshot-migrations-card">
          <button
            onClick={() => setOsmOpen(v => !v)}
            className="w-full flex items-center justify-between gap-3 text-left"
            data-testid="oneshot-toggle"
          >
            <div>
              <h2 className="text-lg font-semibold text-slate-300">Historical one-shot ops migrations</h2>
              <p className="text-xs text-slate-500 mt-1">
                Older operational fixes (QR backfill, status renames, player dedupe, team+age_group, orphan fee_breakdown). Each is idempotent; always Dry Run first.
              </p>
            </div>
            {osmOpen ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
          </button>

          {osmOpen && (
            <div className="mt-4 space-y-3" data-testid="oneshot-list">
              <div className="border border-amber-500/30 bg-amber-500/10 rounded p-3 flex gap-2 items-start text-xs text-amber-200">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <span>These touch production data. Always click <strong>Dry Run</strong> first to preview how many rows will change.</span>
              </div>
              {ONESHOT_MIGRATIONS.map(mig => {
                const status = osmRunning[mig.id];
                const result = osmResults[mig.id];
                return (
                  <div key={mig.id} className="border border-slate-800 bg-slate-950/40 rounded p-3" data-testid={`oneshot-card-${mig.id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm text-slate-100">{mig.name}</h3>
                        <p className="text-xs text-slate-400 mt-1">{mig.description}</p>
                        <p className="text-[10px] text-slate-500 mt-1 font-mono break-all">POST {mig.endpoint}</p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          size="sm" variant="outline"
                          className="border-slate-600 text-slate-300 hover:bg-slate-800"
                          onClick={() => callOneshotMigration(mig, true)}
                          disabled={!!status}
                          data-testid={`oneshot-dry-${mig.id}`}
                        >
                          {status === "dry" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1" />}
                          Dry Run
                        </Button>
                        <Button
                          size="sm"
                          className="bg-rose-600 hover:bg-rose-500 text-white"
                          onClick={() => {
                            if (!window.confirm(`Run "${mig.name}"?\n\nThis modifies production data. You've done a dry-run, right?`)) return;
                            callOneshotMigration(mig, false);
                          }}
                          disabled={!!status}
                          data-testid={`oneshot-run-${mig.id}`}
                        >
                          {status === "run" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1" />}
                          Run
                        </Button>
                      </div>
                    </div>

                    {result && (
                      <div className="mt-3 pt-3 border-t border-slate-800" data-testid={`oneshot-result-${mig.id}`}>
                        <div className="flex items-center gap-2 mb-2">
                          {result.error ? (
                            <Badge variant="destructive">ERROR</Badge>
                          ) : (
                            <Badge className={result.dry ? "bg-slate-700" : "bg-emerald-700"}>
                              {result.dry ? "Dry Run" : "Completed"}
                            </Badge>
                          )}
                          <span className="text-[10px] text-slate-500">{new Date(result.at).toLocaleString()}</span>
                        </div>
                        {result.error ? (
                          <p className="text-xs text-rose-300">{result.error}</p>
                        ) : (
                          <div className="grid grid-cols-4 gap-2 text-xs">
                            <Stat label="Candidates" value={result.total_candidates ?? 0} accent="slate" />
                            <Stat label="Updated" value={result.updated ?? 0} accent="emerald" />
                            <Stat label="Skipped" value={result.skipped ?? 0} accent="slate" />
                            <Stat label="Errored" value={result.errored ?? 0} accent={(result.errored ?? 0) > 0 ? "rose" : "slate"} />
                          </div>
                        )}
                        {result.dry && !result.error && (
                          <div className="mt-2 p-2 rounded bg-emerald-500/5 border border-emerald-500/20 flex items-center gap-2">
                            <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                            <p className="text-[11px] text-emerald-300">Dry-run looks good. Click <strong>Run</strong> to apply {result.total_candidates ?? 0} change(s).</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "slate" }) {
  const accentClass = {
    emerald: "border-emerald-500/40 text-emerald-300",
    amber: "border-amber-500/40 text-amber-300",
    rose: "border-rose-500/40 text-rose-300",
    blue: "border-blue-500/40 text-blue-300",
    slate: "border-slate-700 text-slate-300",
  }[accent] || "border-slate-700 text-slate-300";
  return (
    <div className={`border ${accentClass} bg-slate-900/40 rounded p-3`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-2xl font-mono mt-1">{value ?? "—"}</div>
    </div>
  );
}
